/**
 * Helper functions for handling facility and view URLs in HeatWatch.
 */

/**
 * Parses the current window location to extract view mode and facility ID if present.
 * Supports path routing (/facility/:id, /about, /methodology, /stories)
 * as well as query parameters (?facility=:id or ?facility_id=:id).
 *
 * @returns {{ view: string, facilityId: string | null }}
 */
export function parseCurrentUrl() {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check for facility ID in query params (e.g. ?facility=xyz or ?facility_id=xyz)
  const queryFacilityId = searchParams.get('facility') || searchParams.get('facility_id');
  if (queryFacilityId) {
    return { view: 'markers', facilityId: queryFacilityId };
  }

  // 2. Check for path-based facility route: /facility/:id
  const facilityPathMatch = pathname.match(/^\/facility\/([^/]+)/i);
  if (facilityPathMatch && facilityPathMatch[1]) {
    return { view: 'markers', facilityId: decodeURIComponent(facilityPathMatch[1]) };
  }

  // 3. Check for main views (/about, /methodology, /stories)
  const cleanPath = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (cleanPath === 'about') {
    return { view: 'about', facilityId: null };
  }
  if (cleanPath === 'methodology') {
    return { view: 'methodology', facilityId: null };
  }
  if (cleanPath === 'stories') {
    return { view: 'stories', facilityId: null };
  }

  // Default to markers (map view) with no selected facility
  return { view: 'markers', facilityId: null };
}

/**
 * Generates the URL path for a facility.
 * @param {string} facilityId 
 * @returns {string} e.g. "/facility/846c8acc-4e71-4ef9-b003-a26b409cf721"
 */
export function getFacilityUrl(facilityId) {
  if (!facilityId) return '/';
  return `/facility/${encodeURIComponent(facilityId)}`;
}

/**
 * Generates the URL path for a main view tab.
 * @param {string} view 
 * @returns {string} e.g. "/about", "/methodology", "/stories", or "/"
 */
export function getViewUrl(view) {
  if (!view || view === 'markers') return '/';
  return `/${view}`;
}

/**
 * Safely updates browser URL using History API without triggering a full page reload.
 * @param {string} path - Target path or relative URL
 * @param {boolean} [replace=false] - Whether to use replaceState instead of pushState
 */
export function updateBrowserUrl(path, replace = false) {
  if (window.location.pathname + window.location.search === path) {
    return;
  }
  if (replace) {
    window.history.replaceState({ path }, '', path);
  } else {
    window.history.pushState({ path }, '', path);
  }
}
